import { toJson, type JsonObject } from "@bufbuild/protobuf";
import { ValueSchema } from "@bufbuild/protobuf/wkt";
import parse from "parse-duration";
import { gte, valid } from "semver";
import { get, type Writable } from "svelte/store";
import { Operator, type Behavior, type Page, type SetAttributeAction, type StateTrigger, type TimeoutTrigger } from "#gen/pages/v2/page_pb.js";
import { ActionTypes, TriggerTypes } from "#lib/components/flow/editor/builder/properties/page/constants.js";
import { deepClone } from "#lib/deepClone.js";
import { setValue, structValue, unsetValue } from "#lib/struct.js";

/** True when the version segment of a component typeUri is >= targetVersion (semver). */
export function isTypeUriVersionAtLeast(typeUri: string | undefined, targetVersion: string): boolean {
	const version = typeUri?.split("/").at(-1);
	return Boolean(version && valid(version) && gte(version, targetVersion));
}

export function setComponentAttribute(page: Writable<Page>, pageState: Writable<Record<string, unknown>>, behavior: Behavior) {
	const action = behavior.action?.value.value as SetAttributeAction;
	if (action) {
		page.update(p => {
			const config = deepClone(p.config ?? {});
			const componentConfig = structValue(config, action.component);
			const value = action.value ? toJson(ValueSchema, action.value) : undefined;
			if (value !== undefined) {
				setValue(componentConfig, action.attribute, value);
			} else {
				unsetValue(componentConfig, action.attribute);
			}
			p.config = config;
			return p;
		});
	}
}

export function mountTimeoutTrigger(page: Writable<Page>, pageState: Writable<Record<string, unknown>>, behavior: Behavior): (() => void) | undefined {
	const timeoutTrigger = behavior.trigger?.value?.value as TimeoutTrigger;
	const timeout = parse(timeoutTrigger?.timeout);
	const action = ActionTypes.find(a => a.value === behavior.action?.value.case);
	if (action && timeout !== null) {
		const timeoutId = setTimeout(() => {
			action.do(page, pageState, behavior);
		}, timeout);
		return () => {
			clearTimeout(timeoutId);
		};
	}
	return undefined;
}

export function mountStateTrigger(page: Writable<Page>, pageState: Writable<Record<string, unknown>>, behavior: Behavior): (() => void) | undefined {
	const stateTrigger = behavior.trigger?.value?.value as StateTrigger;
	const action = ActionTypes.find(a => a.value === behavior.action?.value.case);
	if (action && stateTrigger?.component) {
		return pageState.subscribe(value => {
			if (stateTrigger?.component) {
				const component = value[stateTrigger.component] as JsonObject;
				const state = component["state"];
				switch (stateTrigger.operator) {
					case Operator.UNSPECIFIED:
					case Operator.EQUALS:
						if (state === stateTrigger.state) {
							action.do(page, pageState, behavior);
						}
						break;
					case Operator.NOT_EQUALS:
						if (state !== stateTrigger.state) {
							action.do(page, pageState, behavior);
						}
						break;
				}
			}
		});
	}
	return undefined;
}

export function mountBehaviors(store: Writable<Page>, pageState: Writable<Record<string, unknown>>): () => void {
	const page = get(store);
	const cleanupFns: (() => void)[] = [];
	for (const behavior of page.behaviors) {
		const trigger = TriggerTypes.find(t => t.value === behavior.trigger?.value.case);
		if (trigger) {
			const cleanup = trigger.mount(store, pageState, behavior);
			if (cleanup) {
				cleanupFns.push(cleanup);
			}
		}
	}
	return () => {
		for (const fn of cleanupFns) {
			fn?.();
		}
	};
}
